import { useState, useRef } from 'react';
import { parseFile, detectStructure, selectBestSheet, transpose } from '../../utils/sprintImport';
import type { DetectionResult, SprintInfo } from '../../utils/sprintImport';
import DetectionPreview from './DetectionPreview';

interface FileImportModalProps {
  sprints: SprintInfo[];
  existingMetricsCount: number;
  onImport: (result: DetectionResult) => void;
  onBulkEdit: (result: DetectionResult) => void;
  onClose: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
];
const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

export default function FileImportModal({
  sprints,
  existingMetricsCount,
  onImport,
  onBulkEdit,
  onClose,
}: FileImportModalProps) {
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DetectionResult | null>(null);
  const [originalData, setOriginalData] = useState<string[][] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) return 'File is too large. Maximum size is 5MB.';
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext) && !ACCEPTED_TYPES.includes(file.type)) {
      return `Unsupported file type. Please use ${ACCEPTED_EXTENSIONS.join(', ')}`;
    }
    return null;
  };

  const processFile = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setParsing(true);
    try {
      const { sheets, data } = await parseFile(file);
      const sprintNames = sprints.map((s) => s.name);
      const bestSheet = selectBestSheet(sheets, data, sprintNames);
      const sheetData = data.get(bestSheet) || [];

      if (sheetData.length === 0) {
        setError('The file appears to be empty. Try a different sheet or file.');
        setParsing(false);
        return;
      }

      setOriginalData(sheetData);
      const detected = detectStructure(sheetData, sprints);
      detected.sheetName = bestSheet;
      setResult(detected);
    } catch {
      setError('Failed to parse file. It may be corrupted or password-protected.');
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleSwapOrientation = () => {
    if (!originalData) return;
    // Re-run detection with transposed hint by transposing the data
    const swapped = transpose(originalData);
    const detected = detectStructure(swapped, sprints);
    detected.orientation = result?.orientation === 'normal' ? 'transposed' : 'normal';
    detected.transposed = detected.orientation === 'transposed';
    setResult(detected);
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 overflow-y-auto">
      <div className="card max-w-2xl w-full mx-4 my-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Import Sprint Metrics</h2>
          <button onClick={onClose} className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {result ? (
          <DetectionPreview
            result={result}
            sprints={sprints}
            existingMetricsCount={existingMetricsCount}
            onImport={onImport}
            onBulkEdit={onBulkEdit}
            onDiscard={onClose}
            onSwapOrientation={handleSwapOrientation}
          />
        ) : (
          <>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragging
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-300 dark:border-dark-500 hover:border-gray-400 dark:hover:border-dark-400'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              {parsing ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Parsing file...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Drag & drop your file here
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">or click to browse</p>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    Supports: .xlsx, .xls, .csv (max 5MB)
                  </p>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              className="hidden"
              onChange={handleFileInput}
            />

            {error && (
              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button onClick={onClose} className="btn-outline text-sm">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
